import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'
import { motion } from 'framer-motion'

interface StepProgressBarProps {
	currentStep: number
	labels: string[]
}

const StepDot = ({
	isCompleted,
	isCurrent,
	stepNumber,
}: {
	isCompleted: boolean
	isCurrent: boolean
	stepNumber: number
}) => (
	<motion.div
		layout
		className={cn(
			'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold transition-colors',
			isCompleted && 'border-primary bg-primary text-primary-foreground',
			isCurrent && 'border-primary bg-primary/10 text-primary',
			!isCompleted && !isCurrent && 'border-muted bg-muted/50 text-muted-foreground',
		)}
		transition={{ duration: 0.3 }}
	>
		{isCompleted ? (
			<motion.span
				key="check"
				initial={{ scale: 0 }}
				animate={{ scale: 1 }}
				transition={{ type: 'spring', stiffness: 300, damping: 20 }}
			>
				<Check className="h-4 w-4" />
			</motion.span>
		) : (
			stepNumber
		)}
	</motion.div>
)

export function StepProgressBar({ currentStep, labels }: StepProgressBarProps) {
	return (
		<div className="flex items-start justify-center gap-0">
			{labels.map((label, i) => {
				const stepNumber = i + 1
				const isCompleted = stepNumber < currentStep
				const isCurrent = stepNumber === currentStep

				return (
					<div key={label} className="flex items-start">
						<div className="flex flex-col items-center gap-1.5">
							<StepDot
								isCompleted={isCompleted}
								isCurrent={isCurrent}
								stepNumber={stepNumber}
							/>
							<span
								className={cn(
									'max-w-24 text-center text-[11px] leading-tight transition-colors',
									isCurrent ? 'text-primary font-medium' : 'text-muted-foreground',
								)}
							>
								{label}
							</span>
						</div>

						{stepNumber < labels.length && (
							<div className="relative mt-3.5 h-0.5 w-12 overflow-hidden bg-muted sm:w-16">
								<motion.div
									className="bg-primary absolute inset-y-0 left-0"
									initial={false}
									animate={{ width: isCompleted ? '100%' : '0%' }}
									transition={{ duration: 0.4, ease: 'easeInOut' }}
								/>
							</div>
						)}
					</div>
				)
			})}
		</div>
	)
}
